/* eslint-disable prettier/prettier */
import React from 'react';
import { Box, Flex, Text, Icon, Badge, HStack, VStack, SimpleGrid } from '@chakra-ui/react';
import { Buildings, CurrencyDollarSimple, Receipt } from 'phosphor-react';
import { ICompany } from '../../../../../hooks/useCompanies';
import {
  ITaxAnalysisCompany,
  formatarEmReais,
  formatarPercentual,
  TAX_COLORS,
} from '../../../../../types/taxAnalysis';
import { formatCNPJ } from '../../../../../utils/FormatFunctions';

interface TaxCardProps {
  company: ICompany;
  companyWithTax: ITaxAnalysisCompany;
  // eslint-disable-next-line no-unused-vars
  onClick: (company: ICompany) => void;
}

function TaxCardComponent({ company, companyWithTax, onClick }: TaxCardProps) {
  const { impostos, total_faturamento: totalFaturamento } = companyWithTax;
  const totalPercentual = impostos.total_percentual;

  const getProgressColor = () => {
    if (totalPercentual > 30) return 'red';
    if (totalPercentual > 20) return 'orange';
    return 'green';
  };

  return (
    <Box
      bg="white"
      borderRadius="lg"
      border="1px"
      borderColor="gray.200"
      p={2.5}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: 'blue.300',
        boxShadow: 'sm',
        transform: 'translateY(-1px)',
      }}
      onClick={() => onClick(company)}
      minW={0}
      w="100%"
    >
      {/* Header compacto com ID */}
      <Flex justify="flex-start" align="center" mb={1.5}>
        <Text fontSize="2xs" fontWeight="600" color="gray.500" noOfLines={1} minW={0}>
          ID: {company.external_id}
        </Text>
      </Flex>

      {/* Nome da Empresa compacto */}
      <Flex align="center" gap={1.5} mb={1} minW={0}>
        <Icon as={Buildings} color="blue.500" boxSize={3.5} flexShrink={0} />
        <Text fontSize="xs" fontWeight="600" color="gray.800" noOfLines={1} minW={0}>
          {company.razao_social}
        </Text>
      </Flex>

      {/* CNPJ compacto */}
      <Text fontSize="2xs" color="gray.400" mb={2} fontFamily="mono" noOfLines={1}>
        {formatCNPJ(company.cnpj)}
      </Text>

      {/* Faturamento e Total Impostos na mesma linha - compacto */}
      <Flex gap={1} mb={2}>
        {/* Faturamento Total */}
        <Box bg="blue.50" px={1.5} py={1} borderRadius="md" flex={1} minW={0}>
          <Flex align="center" gap={1}>
            <Icon as={CurrencyDollarSimple} color="blue.500" boxSize={3} flexShrink={0} />
            <VStack align="flex-start" spacing={0} flex={1} minW={0}>
              <Text fontSize="2xs" color="blue.600" fontWeight="500" noOfLines={1}>
                Faturamento
              </Text>
              <Text fontSize="2xs" fontWeight="700" color="blue.700" noOfLines={1}>
                {formatarEmReais(totalFaturamento)}
              </Text>
            </VStack>
          </Flex>
        </Box>

        {/* Total de Impostos */}
        <Box bg="gray.50" px={1.5} py={1} borderRadius="md" flex={1} minW={0}>
          <Flex direction="column" gap={0.5}>
            <Flex align="center" gap={1}>
              <Icon as={Receipt} color="gray.600" boxSize={3} flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1} flex={1}>
                Impostos
              </Text>
              <Badge
                colorScheme={getProgressColor()}
                fontSize="2xs"
                px={1}
                borderRadius="full"
                flexShrink={0}
              >
                {formatarPercentual(totalPercentual)}
              </Badge>
            </Flex>
            <Text fontSize="2xs" fontWeight="700" color="gray.700" noOfLines={1} pl={4}>
              {formatarEmReais(impostos.total_impostos)}
            </Text>
          </Flex>
        </Box>
      </Flex>

      {/* Grid de Impostos Individuais - compacto e fino */}
      <Box borderTop="1px" borderColor="gray.100" pt={2}>
        <Text
          fontSize="2xs"
          fontWeight="600"
          color="gray.500"
          mb={1.5}
          textTransform="uppercase"
          letterSpacing="0.3px"
        >
          Detalhamento dos Impostos
        </Text>
        <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={1}>
          {/* PIS */}
          <Flex
            direction="column"
            align="center"
            justify="center"
            bg="gray.50"
            px={1}
            py={1}
            borderRadius="sm"
            minW={0}
            gap={0.5}
          >
            <HStack spacing={0.5} justify="center" w="100%">
              <Box w={1.5} h={1.5} borderRadius="full" bg={TAX_COLORS.PIS} flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                PIS
              </Text>
            </HStack>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.700"
              textAlign="center"
              noOfLines={1}
              wordBreak="break-word"
            >
              {formatarEmReais(impostos.pis)}
            </Text>
          </Flex>
          {/* COFINS */}
          <Flex
            direction="column"
            align="center"
            justify="center"
            bg="gray.50"
            px={1}
            py={1}
            borderRadius="sm"
            minW={0}
            gap={0.5}
          >
            <HStack spacing={0.5} justify="center" w="100%">
              <Box w={1.5} h={1.5} borderRadius="full" bg={TAX_COLORS.COFINS} flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                COFINS
              </Text>
            </HStack>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.700"
              textAlign="center"
              noOfLines={1}
              wordBreak="break-word"
            >
              {formatarEmReais(impostos.cofins)}
            </Text>
          </Flex>
          {/* CSLL */}
          <Flex
            direction="column"
            align="center"
            justify="center"
            bg="gray.50"
            px={1}
            py={1}
            borderRadius="sm"
            minW={0}
            gap={0.5}
          >
            <HStack spacing={0.5} justify="center" w="100%">
              <Box w={1.5} h={1.5} borderRadius="full" bg={TAX_COLORS.CSLL} flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                CSLL
              </Text>
            </HStack>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.700"
              textAlign="center"
              noOfLines={1}
              wordBreak="break-word"
            >
              {formatarEmReais(impostos.csll)}
            </Text>
          </Flex>
          {/* IRPJ */}
          <Flex
            direction="column"
            align="center"
            justify="center"
            bg="gray.50"
            px={1}
            py={1}
            borderRadius="sm"
            minW={0}
            gap={0.5}
          >
            <HStack spacing={0.5} justify="center" w="100%">
              <Box w={1.5} h={1.5} borderRadius="full" bg={TAX_COLORS.IRPJ} flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                IRPJ
              </Text>
            </HStack>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.700"
              textAlign="center"
              noOfLines={1}
              wordBreak="break-word"
            >
              {formatarEmReais(impostos.irpj)}
            </Text>
          </Flex>
          {/* ICMS */}
          <Flex
            direction="column"
            align="center"
            justify="center"
            bg="gray.50"
            px={1}
            py={1}
            borderRadius="sm"
            minW={0}
            gap={0.5}
          >
            <HStack spacing={0.5} justify="center" w="100%">
              <Box w={1.5} h={1.5} borderRadius="full" bg={TAX_COLORS.ICMS} flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                ICMS
              </Text>
            </HStack>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.700"
              textAlign="center"
              noOfLines={1}
              wordBreak="break-word"
            >
              {formatarEmReais(impostos.icms)}
            </Text>
          </Flex>
          {/* ISS */}
          <Flex
            direction="column"
            align="center"
            justify="center"
            bg="gray.50"
            px={1}
            py={1}
            borderRadius="sm"
            minW={0}
            gap={0.5}
          >
            <HStack spacing={0.5} justify="center" w="100%">
              <Box w={1.5} h={1.5} borderRadius="full" bg="#FF9F0A" flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                ISS
              </Text>
            </HStack>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.700"
              textAlign="center"
              noOfLines={1}
              wordBreak="break-word"
            >
              {formatarEmReais(impostos.iss)}
            </Text>
          </Flex>
          {/* ISS Retido */}
          <Flex
            direction="column"
            align="center"
            justify="center"
            bg="gray.50"
            px={1}
            py={1}
            borderRadius="sm"
            minW={0}
            gap={0.5}
          >
            <HStack spacing={0.5} justify="center" w="100%">
              <Box w={1.5} h={1.5} borderRadius="full" bg="#BF5AF2" flexShrink={0} />
              <Text fontSize="2xs" color="gray.500" fontWeight="500" noOfLines={1}>
                ISS Retido
              </Text>
            </HStack>
            <Text
              fontSize="2xs"
              fontWeight="700"
              color="gray.700"
              textAlign="center"
              noOfLines={1}
              wordBreak="break-word"
            >
              {formatarEmReais(impostos.iss_retido)}
            </Text>
          </Flex>
        </SimpleGrid>
      </Box>
    </Box>
  );
}

export const TaxCard = React.memo(TaxCardComponent, (prevProps, nextProps) => {
  // Comparação customizada para evitar re-renders desnecessários
  return (
    prevProps.company.id === nextProps.company.id &&
    prevProps.company.external_id === nextProps.company.external_id &&
    prevProps.companyWithTax.external_id === nextProps.companyWithTax.external_id &&
    prevProps.companyWithTax.total_faturamento === nextProps.companyWithTax.total_faturamento &&
    prevProps.companyWithTax.impostos.total_impostos ===
      nextProps.companyWithTax.impostos.total_impostos &&
    prevProps.companyWithTax.impostos.total_percentual ===
      nextProps.companyWithTax.impostos.total_percentual
  );
});
